import { z } from 'zod';
// Brand-video-signal aggregate (s233 — phase B of creative-duration-
// intelligence). One row per brand, recomputed by the daily meta-
// engagement-digest scheduler. Feeds mag-creative-drop as a compact
// "your video history says X" prompt-line so duration/format calls
// have a real signal to lean on instead of fabricating.
//
// What we explicitly DO NOT do here:
//   - bucket by video duration. IG Graph API doesn't expose duration
//     on /media; probing mp4 headers has 10-20% failure rate. Phase B
//     scope shifted to "does video work for this brand, and which
//     placement" — duration learning lives in phase C (our own ads,
//     known durations, conversion data). See docs/creative-duration-
//     intelligence.md "Decided (s233)" block.
//   - persist raw per-post insights. The engagement digest already
//     does that work each day; this aggregate is the dist down to
//     what the drop agent actually needs.
/** One bucket in the aggregate. `kind` lines up with IG's
 *  `mediaProductType` values plus a synthetic 'static_image' bucket
 *  for non-video posts (useful as the baseline "what video has to
 *  beat" reference). */
export const brandVideoSignalBucketSchema = z.object({
    /** REEL = vertical reel (~9:16, short-form). VIDEO = single feed
     *  video (typically square or 4:5). CAROUSEL_ALBUM = multi-slide
     *  posts (which may contain mixed media). static_image = baseline
     *  for the agent to read engagement-rate context. */
    kind: z.enum(['REEL', 'VIDEO', 'CAROUSEL_ALBUM', 'static_image']),
    /** Posts in this bucket within the sample window. */
    postCount: z.number().int().min(0),
    /** Median (likeCount + commentsCount) per post in this bucket.
     *  Median, not mean — IG counts are heavy-tailed (one viral post
     *  would skew the mean badly). Null when bucket is empty.
     *  v1 (s233) uses raw counts because reach-normalized rates require
     *  per-post insights we don't yet cache at 90d depth; v2 will swap
     *  in a reach-normalized rate once that cache exists. */
    medianInteractions: z.number().min(0).nullable(),
    /** Permalink of the bucket's top performer (by total interactions).
     *  Mag cites it in reasoning so the founder can verify on IG.
     *  Null when the bucket is empty. */
    topPermalink: z.string().url().nullable(),
});
/** Duration range buckets for own-publish slicing (s233.7 / phase B
 *  v2 #3). Three coarse ranges because finer slicing dilutes signal
 *  for any individual brand — most brands won't accumulate enough
 *  per-second datapoints. */
export const brandVideoDurationRangeEnum = z.enum(['≤5s', '6-8s', '9-12s']);
/** One (format × duration) bucket populated from Mag's own
 *  organic-meta publishes (s233.7). Distinct from the
 *  format-only buckets above because the duration is only
 *  knowable for posts WE published (Meta doesn't expose video
 *  duration on /media for arbitrary posts). */
export const brandVideoSignalOwnPublishBucketSchema = z.object({
    format: z.enum(['REEL', 'VIDEO']),
    durationRange: brandVideoDurationRangeEnum,
    postCount: z.number().int().min(0),
    /** Median (likeCount + commentsCount) across the bucket's posts.
     *  Null when bucket empty. Median for the same heavy-tail
     *  reason as the format-only buckets above. */
    medianInteractions: z.number().min(0).nullable(),
    /** Permalink of the bucket's top performer. Null when empty. */
    topPermalink: z.string().url().nullable(),
});
export const brandVideoSignalAggregateSchema = z.object({
    /** Buckets in a stable order — REEL first (the format we expect to
     *  matter most for paid creative learnings), then VIDEO, then
     *  CAROUSEL_ALBUM, then static_image baseline. */
    buckets: z.array(brandVideoSignalBucketSchema).max(4),
    /** Posts that made it into ANY bucket (post-threshold-filter). The
     *  agent reads this to decide whether the signal is meaningful —
     *  see brandVideoSignalIsThin() below. */
    postsAnalyzed: z.number().int().min(0),
    /** Days back from generatedAt that we sampled. Currently always
     *  90; stored so future widening is observable from the row. */
    sampleWindowDays: z.number().int().min(1).max(365),
    /** ISO timestamp the aggregate was computed. Drives the "refreshed
     *  Xh ago" label in the drop preview UI. */
    generatedAt: z.string(),
    /** s233.7 (phase B v2 #3) — per-(format × duration) buckets from
     *  Mag's own published organic posts. Populated when the scheduler
     *  finds organic_meta slots whose draft.publishedPostId matches an
     *  IG post we read insights for. Optional + nullable so existing
     *  aggregate rows from s233 still validate. */
    ownPublishBuckets: z.array(brandVideoSignalOwnPublishBucketSchema).max(6).optional(),
});
/** Minimum analyzed-posts count for the agent to treat the signal as
 *  load-bearing. Below this the runner emits `audienceVideoSignal:
 *  null` and the agent uses the cold-start branch. Picked at 5
 *  because anything less is single-post noise. */
export const BRAND_VIDEO_SIGNAL_THIN_THRESHOLD = 5;
/** True when the aggregate is too thin to lean on. The runner uses
 *  this to decide whether to format the signal into the agent prompt
 *  or pass null (cold-start branch). */
export function brandVideoSignalIsThin(agg) {
    if (!agg)
        return true;
    return agg.postsAnalyzed < BRAND_VIDEO_SIGNAL_THIN_THRESHOLD;
}
/** Compute the format-bucket aggregate from a batch of IG posts.
 *  Caller passes a 90d-sliced batch (we don't filter by timestamp
 *  here — that's the scheduler's job).
 *
 *  Per-bucket "performance" is the median of (likeCount +
 *  commentsCount) across the bucket's posts. Median (not mean)
 *  resists the heavy tail of IG counts: a single viral post would
 *  pull a mean way up and lie about typical-post performance.
 *
 *  Top permalink within a bucket is the single post with the highest
 *  total interactions — Mag cites it in reasoning so the founder can
 *  see "your top reel was X" with a verifiable link.
 *
 *  s233.7 — when an `ownPublishes` map is provided, we additionally
 *  bucket Mag's own publishes by (format × duration range) and
 *  populate aggregate.ownPublishBuckets. The map's keys are IG
 *  media ids (= post.id from the Graph fetch). Posts not in the
 *  map are skipped from the own-publish pass but still contribute
 *  to the format-only buckets above. */
export function aggregateMetaVideoSignal(posts, opts) {
    const sortedKinds = [
        'REEL',
        'VIDEO',
        'CAROUSEL_ALBUM',
        'static_image',
    ];
    const byKind = new Map();
    for (const k of sortedKinds)
        byKind.set(k, []);
    for (const post of posts) {
        byKind.get(classifyPost(post))?.push(post);
    }
    const buckets = [];
    let postsAnalyzed = 0;
    for (const kind of sortedKinds) {
        const inBucket = byKind.get(kind) ?? [];
        if (inBucket.length === 0)
            continue;
        postsAnalyzed += inBucket.length;
        const interactions = inBucket.map((p) => p.likeCount + p.commentsCount).sort((a, b) => a - b);
        const median = interactions.length === 0 ? null : medianOfSorted(interactions);
        // Top performer — pick the single highest-total post for the
        // permalink. Stable: ties resolve to first-seen so re-runs over
        // the same data return the same row.
        let topPermalink = null;
        let topScore = -1;
        for (const p of inBucket) {
            const score = p.likeCount + p.commentsCount;
            if (score > topScore) {
                topScore = score;
                topPermalink = p.permalink;
            }
        }
        buckets.push({
            kind,
            postCount: inBucket.length,
            medianInteractions: median,
            topPermalink,
        });
    }
    const ownPublishBuckets = opts.ownPublishes
        ? computeOwnPublishBuckets(posts, opts.ownPublishes)
        : undefined;
    return {
        buckets,
        postsAnalyzed,
        sampleWindowDays: opts.sampleWindowDays,
        generatedAt: opts.generatedAt ?? new Date().toISOString(),
        ...(ownPublishBuckets && ownPublishBuckets.length > 0 ? { ownPublishBuckets } : {}),
    };
}
/** Map a duration in seconds to one of the three coarse ranges.
 *  Clamps out-of-range (anything <4 or >12 is unexpected) to the
 *  nearest valid bucket so the caller doesn't have to pre-filter. */
function durationRangeFor(seconds) {
    if (seconds <= 5)
        return '≤5s';
    if (seconds <= 8)
        return '6-8s';
    return '9-12s';
}
/** Build per-(format × duration) buckets from posts that match the
 *  own-publish map. Only video posts with a known duration are
 *  bucketed — image posts in the map are skipped (no duration to
 *  slice on). Empty buckets are omitted from the returned array. */
function computeOwnPublishBuckets(posts, ownPublishes) {
    // bucketKey = `${format}::${range}`. Map preserves insertion order
    // so the output is naturally stable as we walk posts.
    const grouped = new Map();
    for (const post of posts) {
        const info = ownPublishes.get(post.id);
        if (!info)
            continue;
        if (info.format !== 'REEL' && info.format !== 'VIDEO')
            continue;
        if (info.durationSeconds == null)
            continue;
        const range = durationRangeFor(info.durationSeconds);
        const key = `${info.format}::${range}`;
        const arr = grouped.get(key) ?? [];
        arr.push(post);
        grouped.set(key, arr);
    }
    const buckets = [];
    for (const [key, inBucket] of grouped.entries()) {
        const [format, range] = key.split('::');
        const interactions = inBucket.map((p) => p.likeCount + p.commentsCount).sort((a, b) => a - b);
        const median = interactions.length === 0 ? null : medianOfSorted(interactions);
        let topPermalink = null;
        let topScore = -1;
        for (const p of inBucket) {
            const score = p.likeCount + p.commentsCount;
            if (score > topScore) {
                topScore = score;
                topPermalink = p.permalink;
            }
        }
        buckets.push({
            format,
            durationRange: range,
            postCount: inBucket.length,
            medianInteractions: median,
            topPermalink,
        });
    }
    return buckets;
}
function classifyPost(post) {
    // mediaProductType is the most specific signal IG gives us; falls
    // back to mediaType when product-type is null (older posts). REELS
    // ship with mediaProductType='REELS' + mediaType='VIDEO'; regular
    // feed videos have mediaProductType='FEED' + mediaType='VIDEO'.
    const productType = post.mediaProductType?.toUpperCase() ?? '';
    if (productType === 'REELS')
        return 'REEL';
    if (post.mediaType === 'CAROUSEL_ALBUM')
        return 'CAROUSEL_ALBUM';
    if (post.mediaType === 'VIDEO')
        return 'VIDEO';
    return 'static_image';
}
function medianOfSorted(sortedAsc) {
    const n = sortedAsc.length;
    if (n === 0)
        return 0;
    const mid = Math.floor(n / 2);
    // biome-ignore lint/style/noNonNullAssertion: bounds guarded above
    if (n % 2 === 1)
        return sortedAsc[mid];
    // biome-ignore lint/style/noNonNullAssertion: bounds guarded above
    return (sortedAsc[mid - 1] + sortedAsc[mid]) / 2;
}
// ─── Prompt formatting ─────────────────────────────────────────────
/** Turn the aggregate into a compact prompt line for mag-creative-
 *  drop. Returns null when the aggregate is thin (caller passes
 *  null into agent context → cold-start branch fires). */
export function formatAudienceVideoSignal(agg) {
    if (brandVideoSignalIsThin(agg) || !agg)
        return null;
    const parts = [];
    for (const b of agg.buckets) {
        if (b.postCount === 0)
            continue;
        const label = bucketLabel(b.kind);
        const interactionPart = b.medianInteractions !== null
            ? `median ${Math.round(b.medianInteractions)} interactions`
            : 'interactions unknown';
        parts.push(`${b.postCount} ${label} (${interactionPart})`);
    }
    if (parts.length === 0)
        return null;
    const baseLine = `last ${agg.sampleWindowDays}d: ${parts.join(' · ')}. signal from ${agg.postsAnalyzed} posts.`;
    // s233.7 — second line from own-publish buckets when any bucket
    // has ≥OWN_PUBLISH_BUCKET_MIN_SAMPLES datapoints. Cold-start rule:
    // below threshold, silence. We don't lie about signal we don't
    // have.
    const ownLine = formatOwnPublishLine(agg);
    return ownLine ? `${baseLine}\n${ownLine}` : baseLine;
}
/** Minimum datapoints in an own-publish bucket before we surface
 *  the line in the prompt. Three because at two posts the median
 *  is just (a+b)/2 — too noisy to trust. */
const OWN_PUBLISH_BUCKET_MIN_SAMPLES = 3;
/** Build the "in my own publishes…" sentence from the
 *  ownPublishBuckets. Returns null when no bucket clears the
 *  min-samples threshold. */
function formatOwnPublishLine(agg) {
    const buckets = agg.ownPublishBuckets ?? [];
    const ready = buckets.filter((b) => b.postCount >= OWN_PUBLISH_BUCKET_MIN_SAMPLES && b.medianInteractions !== null);
    if (ready.length === 0)
        return null;
    const parts = ready.map((b) => {
        const formatLabel = b.format === 'REEL' ? 'reels' : 'feed videos';
        const median = Math.round(b.medianInteractions ?? 0);
        return `your ${formatLabel} at ${b.durationRange} averaged ${median} interactions (n=${b.postCount})`;
    });
    return `in my own publishes: ${parts.join('; ')}.`;
}
function bucketLabel(kind) {
    if (kind === 'REEL')
        return 'reels';
    if (kind === 'VIDEO')
        return 'feed videos';
    if (kind === 'CAROUSEL_ALBUM')
        return 'carousels';
    return 'static posts';
}
//# sourceMappingURL=brand-video-signal.js.map